import {
  BreedDatabaseConfig,
  BreedSize,
  ExclusionRulesConfig,
  SupplementConfig,
  SupplementProfile,
  SupplementRulesConfig,
  SupplementSafetyRule,
} from '../types/config';
import { normalizeBreedKey } from './normalize';
import { buildAliasLookup, buildProteinAliasLookup, canonicalizeToken } from './proteinAliases';

export interface DogProfileForSupplements {
  ageCategory: string;
  activityLevel: string;
  bodyCondition: string;
  pathologies: string[];
  /** Fase 2 — alérgenos declarados (proteínas + marisco/huevo/algas). Gatillan block_if/caution_if. */
  allergens?: string[];
  /** Fase 2 — sub-patologías por patología (p.ej. {sensibilidad_digestiva: ['gastritis_reflujo']}). */
  subPathologies?: Record<string, string[]>;
  breedSize: BreedSize;
  dogName: string;
  breed?: string;
}

export interface SupplementCaution {
  message: string;
  atc: boolean;
}

export interface ScoredSupplement {
  supplement: SupplementConfig;
  score: number;
  isRecommended: boolean;
  recommendationText: string;
  recommendationReasons: string[];
  /** Fase 2 — bloqueado clínicamente para este perro (no se muestra ni es seleccionable). */
  blocked: boolean;
  /** Fase 2 — cautela: nunca auto-recomendado, seleccionable con aviso. null si no aplica. */
  caution: SupplementCaution | null;
}

const MATCHABLE_DIMENSIONS: (keyof SupplementSafetyRule)[] = [
  'pathologies',
  'sub_pathologies',
  'allergens',
  'ages',
  'body_conditions',
];

const NONE_LIKE_VALUES = new Set(['', 'ninguna', 'ninguno', 'none', 'null', 'na', 'n_a']);

function canonicalValues(values: string[] | undefined, lookup: Record<string, string>): Set<string> {
  return new Set(
    (values || [])
      .map((value) => canonicalizeToken(String(value), lookup))
      .filter((value) => value && !NONE_LIKE_VALUES.has(value)),
  );
}

function productIdBlocked(productId: string, ids: string[] | undefined): boolean {
  return new Set((ids || []).map(String)).has(productId);
}

function ruleByCanonicalKey<T>(
  rules: Record<string, T> | undefined,
  canonicalKey: string,
  lookup: Record<string, string>,
): T | undefined {
  return Object.entries(rules || {}).find(([key]) => canonicalizeToken(key, lookup) === canonicalKey)?.[1];
}

function productIdsByCanonicalKey(
  rules: Record<string, { blocked_product_ids?: string[] }> | undefined,
  canonicalKey: string,
  lookup: Record<string, string>,
): string[] {
  return Object.entries(rules || {})
    .filter(([key]) => canonicalizeToken(key, lookup) === canonicalKey)
    .flatMap(([, rule]) => rule.blocked_product_ids || []);
}

function supplementIsExcluded(
  supplement: SupplementConfig,
  dog: DogProfileForSupplements,
  rules: Partial<ExclusionRulesConfig>,
  proteinLookup: Record<string, string>,
  pathologyLookup: Record<string, string>,
): boolean {
  const productId = String(supplement.id);
  const dogAllergens = canonicalValues(dog.allergens, proteinLookup);
  const dogPathologies = canonicalValues(dog.pathologies, pathologyLookup);
  const dogSubPathologies = new Map<string, Set<string>>();
  Object.entries(dog.subPathologies || {}).forEach(([pathology, subPathologies]) => {
    dogSubPathologies.set(canonicalizeToken(pathology, pathologyLookup), canonicalValues(subPathologies, {}));
  });
  const productAllergens = canonicalValues(
    [...(supplement.allergens || []), ...(supplement.profile?.contains_allergens || [])],
    proteinLookup,
  );

  if (Array.from(productAllergens).some((allergen) => dogAllergens.has(allergen))) return true;

  const globallyBlockedPathologies = canonicalValues(rules.blocked_pathologies, pathologyLookup);
  if (Array.from(dogPathologies).some((pathology) => globallyBlockedPathologies.has(pathology))) return true;

  for (const allergen of Array.from(dogAllergens)) {
    const blockedProductIds = productIdsByCanonicalKey(rules.allergen_rules, allergen, proteinLookup);
    if (productIdBlocked(productId, blockedProductIds)) return true;
  }

  for (const pathology of Array.from(dogPathologies)) {
    const legacyRule = ruleByCanonicalKey(rules.pathology_blocked_product_ids, pathology, pathologyLookup);
    if (productIdBlocked(productId, legacyRule)) return true;
    const rule = ruleByCanonicalKey(rules.pathology_rules, pathology, pathologyLookup);
    if (rule?.mode === 'deny_all') return true;
    if (rule?.mode === 'filter_products' && productIdBlocked(productId, rule.blocked_product_ids)) return true;

    for (const subPathology of Array.from(dogSubPathologies.get(pathology) || [])) {
      const subRule = Object.entries(rule?.sub_pathology_rules || {}).find(
        ([key]) => canonicalizeToken(key, {}) === subPathology,
      )?.[1];
      if (subRule?.mode === 'deny_all') return true;
      if (subRule?.mode === 'filter_products' && productIdBlocked(productId, subRule.blocked_product_ids)) return true;
    }
  }
  return false;
}

/**
 * Mirror of `app/services/supplement_safety.py` matching semantics: within a
 * rule all present clinical dimensions must match (AND); a dimension list
 * matches if the dog declares ANY listed value. Non-capturable dimensions are
 * ignored, so a rule with no matchable dimension never fires.
 */
function ruleMatches(
  rule: SupplementSafetyRule,
  pathologies: Set<string>,
  subPathologies: Record<string, string[]>,
  allergens: Set<string>,
  ages: Set<string>,
  bodyConditions: Set<string>,
  proteinLookup: Record<string, string>,
  pathologyLookup: Record<string, string>,
): boolean {
  if (!MATCHABLE_DIMENSIONS.some((dim) => rule[dim] !== undefined)) return false;
  if (rule.allergens && !Array.from(canonicalValues(rule.allergens, proteinLookup)).some((v) => allergens.has(v))) {
    return false;
  }
  if (
    rule.pathologies &&
    !Array.from(canonicalValues(rule.pathologies, pathologyLookup)).some((v) => pathologies.has(v))
  ) {
    return false;
  }
  if (rule.ages && !Array.from(canonicalValues(rule.ages, {})).some((v) => ages.has(v))) return false;
  if (
    rule.body_conditions &&
    !Array.from(canonicalValues(rule.body_conditions, {})).some((v) => bodyConditions.has(v))
  ) {
    return false;
  }
  if (rule.sub_pathologies) {
    const matched = Object.entries(rule.sub_pathologies).some(([pathology, subs]) => {
      const canonicalPathology = canonicalizeToken(pathology, pathologyLookup);
      const wanted = canonicalValues(subs, {});
      const have = canonicalValues(subPathologies[canonicalPathology], {});
      return Array.from(wanted).some((subPathology) => have.has(subPathology));
    });
    if (!matched) return false;
  }
  return true;
}

function evaluateSafety(
  profile: SupplementProfile | undefined,
  dog: DogProfileForSupplements,
  interpolate: (text: string) => string,
  proteinLookup: Record<string, string>,
  pathologyLookup: Record<string, string>,
): { blockedMessage: string | null; caution: SupplementCaution | null } {
  if (!profile) return { blockedMessage: null, caution: null };
  const pathologies = canonicalValues(dog.pathologies, pathologyLookup);
  const allergens = canonicalValues(dog.allergens, proteinLookup);
  const subPathologies: Record<string, string[]> = {};
  Object.entries(dog.subPathologies || {}).forEach(([pathology, values]) => {
    subPathologies[canonicalizeToken(pathology, pathologyLookup)] = Array.from(canonicalValues(values, {}));
  });
  const ages = canonicalValues([dog.ageCategory].filter(Boolean), {});
  const bodyConditions = canonicalValues([dog.bodyCondition].filter(Boolean), {});

  for (const rule of profile.block_if || []) {
    if (
      ruleMatches(rule, pathologies, subPathologies, allergens, ages, bodyConditions, proteinLookup, pathologyLookup)
    ) {
      return { blockedMessage: interpolate(rule.message || ''), caution: null };
    }
  }
  for (const rule of profile.caution_if || []) {
    if (
      ruleMatches(rule, pathologies, subPathologies, allergens, ages, bodyConditions, proteinLookup, pathologyLookup)
    ) {
      return {
        blockedMessage: null,
        caution: { message: interpolate(rule.message || ''), atc: Boolean(rule.atc || rule.atc_referral) },
      };
    }
  }
  return { blockedMessage: null, caution: null };
}

/**
 * Score supplements based on dog profile and supplement rules config.
 * Optionally uses breed_database to apply breed-specific supplement bonuses.
 * Returns active supplements sorted by score (highest first).
 */
export function scoreSupplements(
  dogProfile: DogProfileForSupplements,
  config: SupplementRulesConfig,
  breedDatabase?: BreedDatabaseConfig,
  exclusionRules?: Partial<ExclusionRulesConfig>,
): ScoredSupplement[] {
  const rules = exclusionRules || {};
  const proteinLookup = buildProteinAliasLookup(rules.protein_aliases);
  const pathologyLookup = buildAliasLookup(rules.pathology_aliases);
  const eligibleSupplements = (config.supplements || []).filter(
    (supplement) =>
      supplement.is_active && !supplementIsExcluded(supplement, dogProfile, rules, proteinLookup, pathologyLookup),
  );
  if (!config.enabled) {
    return eligibleSupplements.map((supplement) => ({
      supplement,
      score: 0,
      isRecommended: false,
      recommendationText: '',
      recommendationReasons: [],
      blocked: false,
      caution: null,
    }));
  }

  const weights = config.scoring_weights;
  const activeSupplements = eligibleSupplements;

  // Look up breed-specific supplement recommendations
  const breedKey = dogProfile.breed ? normalizeBreedKey(dogProfile.breed) : '';
  const breedEntry = breedKey && breedDatabase?.breeds?.[breedKey] ? breedDatabase.breeds[breedKey] : null;
  const breedSupplementMap = new Map<string, { text: string; priority: number }>();
  if (breedEntry) {
    for (const rec of breedEntry.supplement_recommendations || []) {
      breedSupplementMap.set(rec.supplement_slug, { text: rec.text, priority: rec.priority });
    }
  }

  const interpolate = (text: string): string => text.replace(/\{dogName\}/g, dogProfile.dogName || 'tu perro');

  const scored: ScoredSupplement[] = activeSupplements.map((supplement) => {
    let score = (supplement.base_priority || 0) * (weights.base_priority || 1);

    // Get the supplement profile (if available) for richer texts. It is embedded
    // per supplement by /app-config now (from the supplement_profiles table), no
    // longer a config map keyed by Shopify variant.
    const profile = supplement.profile;
    const reasons: string[] = [];

    // Pathology matches
    const matchedPathologies = (dogProfile.pathologies || []).filter(
      (p) => p !== 'ninguna' && (supplement.good_for_pathologies || []).includes(p),
    );
    score += matchedPathologies.length * (weights.pathology_match || 0);

    // Add pathology-specific texts
    for (const pathology of matchedPathologies) {
      const pathText = profile?.pathology_texts?.[pathology];
      if (pathText) {
        reasons.push(interpolate(pathText));
      }
    }

    // Age match
    const ageMatch = (supplement.good_for_ages || []).includes(dogProfile.ageCategory);
    if (ageMatch) {
      score += weights.age_match || 0;
      if (profile?.age_text) {
        reasons.push(interpolate(profile.age_text));
      }
    }

    // Body condition match
    const bodyMatch = (supplement.good_for_body_conditions || []).includes(dogProfile.bodyCondition);
    if (bodyMatch) {
      score += weights.body_condition_match || 0;
      if (profile?.body_condition_text) {
        reasons.push(interpolate(profile.body_condition_text));
      }
    }

    // Activity level match
    if ((supplement.good_for_activity_levels || []).includes(dogProfile.activityLevel)) {
      score += weights.activity_match || 0;
    }

    // Breed size match
    const breedSizeMatch = (supplement.good_for_breed_sizes || []).includes(dogProfile.breedSize);
    if (breedSizeMatch) {
      score += weights.breed_size_match || 0;
      if (profile?.breed_size_text) {
        reasons.push(interpolate(profile.breed_size_text));
      }
    }

    // Breed-specific supplement bonus
    const breedRec = supplement.code ? breedSupplementMap.get(supplement.code) : undefined;
    if (breedRec) {
      score += (breedRec.priority || 1) * (weights.pathology_match || 10);
      if (breedRec.text) {
        reasons.push(interpolate(breedRec.text));
      }
    }

    // Fallback: if no specific reasons, use generic recommendation_text
    if (reasons.length === 0 && supplement.recommendation_text) {
      reasons.push(interpolate(supplement.recommendation_text));
    }

    // Fase 2 — guardarraíles clínicos (block_if / caution_if).
    const { blockedMessage, caution } = evaluateSafety(
      profile,
      dogProfile,
      interpolate,
      proteinLookup,
      pathologyLookup,
    );

    return {
      supplement,
      score,
      isRecommended: false,
      recommendationText: reasons[0] || '',
      recommendationReasons: reasons,
      blocked: blockedMessage !== null,
      caution,
    };
  });

  // Fase 2 — un suplemento bloqueado desaparece por completo: fuera de la lista
  // de recomendados Y no seleccionable (no puntúa 0, se elimina del array).
  const visible = scored.filter((item) => !item.blocked);

  // Sort by score descending
  visible.sort((a, b) => b.score - a.score);

  // Mark top N as recommended. Un suplemento en cautela NUNCA se auto-recomienda
  // (queda disponible para selección manual, con su aviso), aunque puntúe alto.
  const maxRecommended = config.max_recommended || 3;
  let recommendedCount = 0;
  visible.forEach((item) => {
    if (recommendedCount < maxRecommended && item.score > 0 && !item.caution) {
      item.isRecommended = true;
      recommendedCount += 1;
    }
  });

  return visible;
}
